import { useState, Dispatch, SetStateAction } from 'react';
import { _deleteLocation } from 'middlewares/LocationApi/location';
import { AlertsProps } from 'components/Alerts/data/Alerts.type';

interface UseLocationDeleteReturn {
  openModal: boolean;
  success: AlertsProps;
  setSuccess: Dispatch<SetStateAction<AlertsProps>>;
  selectedLocationId: number | null;
  selectedLocationName: string;
  handleDelete: (
    locationId: number,
    e: React.MouseEvent,
    locationName?: string,
  ) => void;
  openDialog: () => void;
  handleClose: () => void;
  handleConfirm: () => Promise<void>;
}

export const useLocationDelete = (
  onLocationDeleted?: (locationId: number) => void,
): UseLocationDeleteReturn => {
  const [openModal, setOpenModal] = useState(false);
  const [success, setSuccess] = useState<AlertsProps>({
    show: false,
    severity: 'info',
    message: '',
  });
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
    null,
  );
  const [selectedLocationName, setSelectedLocationName] = useState<string>('');

  const handleDelete = (
    locationId: number,
    e: React.MouseEvent,
    locationName?: string,
  ) => {
    e.stopPropagation();
    setSelectedLocationId(locationId);
    setSelectedLocationName(locationName ?? '');
    openDialog();
  };

  const openDialog = () => {
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
    setSelectedLocationId(null);
    setSelectedLocationName('');
  };

  const deleteLocation = async () => {
    if (selectedLocationId === null) return;

    try {
      const res = await _deleteLocation(selectedLocationId);
      if (res) {
        onLocationDeleted?.(selectedLocationId);

        // Show success message
        setSuccess({
          show: true,
          severity: 'success',
          message: 'Deleted Successfully!',
        });
      }
    } catch (err) {
      console.error('Error:', err);
      // Show error message
      setSuccess({
        show: true,
        severity: 'error',
        message: 'Failed to delete location',
      });
    }
  };

  const handleConfirm = async () => {
    await deleteLocation();
    handleClose();
  };

  return {
    openModal,
    success,
    setSuccess,
    selectedLocationId,
    selectedLocationName,
    handleDelete,
    openDialog,
    handleClose,
    handleConfirm,
  };
};
